import {
  clearCanvasGpuTurn,
  waitForCanvasGpuTurn,
} from "../canvas-gpu-lock";
import {
  destroyWebGpuCanvas,
  requestWebGpuCanvas,
  resizeWebGpuCanvas,
  watchCanvasResize,
} from "../webgpu-canvas";
import { createTimelinePipeline } from "./create-pipeline";
import { drawTimelineFrame } from "./render-frame";
import { startWebGl2Timeline } from "./webgl2-timeline";
import { timelineFrameDetail } from "@/lib/timeline/scene/TimelineFrame/frameDetail";
import { resolveTimelineGpuScene, type TimelineGpuRuntime } from "./runtime-options";
import timelineShader from "./timeline.wgsl?raw";

export type TimelineGpuOptions = TimelineGpuRuntime;

const timelineSession = new WeakMap<HTMLCanvasElement, number>();

export async function initTimelineGpu(
  canvas: HTMLCanvasElement,
  getRuntime: () => TimelineGpuRuntime,
): Promise<() => void> {
  const releaseTurn = await waitForCanvasGpuTurn(canvas);
  const session = (timelineSession.get(canvas) ?? 0) + 1;
  timelineSession.set(canvas, session);
  const isActive = (): boolean => timelineSession.get(canvas) === session;

  const finishEmpty = (): (() => void) => {
    clearCanvasGpuTurn(canvas, releaseTurn);
    return () => undefined;
  };

  const gpu = await requestWebGpuCanvas(canvas);
  if (!isActive()) {
    if (gpu.ok) {
      await destroyWebGpuCanvas(gpu.handle);
    }
    return finishEmpty();
  }
  if (!gpu.ok) {
    const stop = startWebGl2Timeline(canvas, getRuntime);
    canvas.dataset.renderer = stop === null ? "none" : "webgl2";
    canvas.dispatchEvent(new CustomEvent("timeline-renderer"));
    if (stop === null) {
      return finishEmpty();
    }
    return () => {
      stop();
      clearCanvasGpuTurn(canvas, releaseTurn);
    };
  }
  canvas.dataset.renderer = "webgpu";

  const handle = gpu.handle;
  const pipeline = createTimelinePipeline(handle, timelineShader);
  let disposed = false;

  const onResize = (): void => {
    if (!disposed && isActive()) {
      resizeWebGpuCanvas(canvas, handle);
    }
  };
  const unwatch = watchCanvasResize(canvas, onResize);

  const onTimelineFrame = (event: Event): void => {
    if (disposed || !isActive()) {
      return;
    }
    resizeWebGpuCanvas(canvas, handle);
    if (canvas.clientWidth < 1 || canvas.clientHeight < 1) {
      return;
    }

    const options = getRuntime();
    const frameDetail = timelineFrameDetail(event);
    const playback = frameDetail?.playback ?? options.getPlayback();
    const view =
      options.resolveViewTransform?.() ?? options.getViewTransform();
    drawTimelineFrame(
      canvas,
      handle.context,
      handle.device,
      pipeline,
      resolveTimelineGpuScene(options),
      playback,
      options.getViewportWidth(),
      view,
    );
  };

  canvas.addEventListener("timeline-frame", onTimelineFrame);
  onTimelineFrame(new CustomEvent("timeline-frame"));

  const teardown = (): void => {
    canvas.removeEventListener("timeline-frame", onTimelineFrame);
    unwatch();
    pipeline.uniformBuffer.destroy();
    pipeline.vertexBuffer.destroy();
    if (timelineSession.get(canvas) === session) {
      timelineSession.delete(canvas);
    }
    void destroyWebGpuCanvas(handle).finally(() => {
      clearCanvasGpuTurn(canvas, releaseTurn);
    });
  };

  return () => {
    if (disposed) {
      return;
    }
    disposed = true;
    teardown();
  };
}
